import React, { memo } from 'react';
import { ClipboardCheckIcon, BookOpenIcon, TrophyIcon, CheckIcon, LockIcon, ShieldIcon, WifiIcon, SmartphoneIcon, CreditCardIcon, EyeOffIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function HowItWorksPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1322">
      <TopBar data-id="element-1323" />
      <Header data-id="element-1324" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1325">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1326">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1327">
            <h1 className="mb-6 text-5xl md:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1328">
              How Caveo Works
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-1329">
              A simple 3-level education system that builds real digital
              confidence—at your own pace, in plain English.
            </p>
          </div>
        </div>
      </section>

      {/* The 3-Step Journey */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1330">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1331">
          <div className="text-center mb-16" data-id="element-1332">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1333">
              Your journey to digital confidence
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1334">
              Three simple steps. No tech degree required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1335">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1336">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1337">
                1
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1338">
                <ClipboardCheckIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1339" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1340">Assess</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1341">
                Answer simple questions about your devices, habits, and
                concerns. In under 3 minutes, we'll create a personalized
                security plan.
              </p>
              <ul className="text-left space-y-2" data-id="element-1342">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1343">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1344" />
                  No technical knowledge needed
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1345">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1346" />
                  Skip questions that don't apply
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1347">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1348" />
                  Get your Security Score instantly
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1349">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1350">
                2
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1351">
                <BookOpenIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1352" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1353">Guide</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1354">
                Follow bite-sized lessons with clear visuals and step-by-step
                instructions. Learn at your own pace—no pressure.
              </p>
              <ul className="text-left space-y-2" data-id="element-1355">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1356">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1357" />
                  Plain English, no jargon
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1358">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1359" />
                  Visual guides show exactly what to do
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1360">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1361" />
                  Pause and resume anytime
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1362">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1363">
                3
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1364">
                <TrophyIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1365" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1366">
                Empower
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1367">
                Track your progress, celebrate wins, and watch your confidence
                grow. See your Security Score improve over time.
              </p>
              <ul className="text-left space-y-2" data-id="element-1368">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1369">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1370" />
                  Family dashboard shows everyone's progress
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1371">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1372" />
                  Celebrate milestones together
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1373">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1374" />
                  Build lasting independence
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 3-Level Education System */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1375">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1376">
          <div className="text-center mb-16" data-id="element-1377">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1378">
              Our 3-Level Education System
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1379">
              39 modules across three skill levels. Start where you are, grow at
              your pace.
            </p>
          </div>

          {/* Level 1 */}
          <div className="mb-12" data-id="element-1380">
            <div className="flex items-center mb-6" data-id="element-1381">
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1382">
                Level 1
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1383">Essentials</h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1384">
                9 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1385">
              The fundamentals everyone needs. Perfect for beginners or anyone
              wanting to solidify their basics.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1386">
              {[{
              icon: LockIcon,
              title: 'Creating Bulletproof Passwords',
              desc: 'Learn to create and manage strong passwords without memorizing them all'
            }, {
              icon: ShieldIcon,
              title: 'Recognizing Scams & Phishing',
              desc: 'Spot fake emails, texts, and calls before they trick you'
            }, {
              icon: WifiIcon,
              title: 'Securing Your Home WiFi',
              desc: 'Protect your network from unwanted visitors'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1387">
                  <module.icon className="w-6 h-6 text-green-600 mb-3" data-id="element-1388" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1389">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1390">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 2 */}
          <div className="mb-12" data-id="element-1391">
            <div className="flex items-center mb-6" data-id="element-1392">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1393">
                Level 2
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1394">
                Intermediate
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1395">
                11 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1396">
              Build on your foundation with more advanced protection strategies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1397">
              {[{
              icon: CreditCardIcon,
              title: 'Protecting Financial Accounts',
              desc: 'Secure your bank accounts, credit cards, and investment apps'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Device Security',
              desc: 'Keep your phone and tablet safe from threats'
            }, {
              icon: EyeOffIcon,
              title: 'Privacy Settings Deep Dive',
              desc: 'Take control of what apps and websites know about you'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1398">
                  <module.icon className="w-6 h-6 text-blue-600 mb-3" data-id="element-1399" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1400">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1401">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 3 */}
          <div data-id="element-1402">
            <div className="flex items-center mb-6" data-id="element-1403">
              <div className="bg-[#631bb7] bg-opacity-20 text-[#631bb7] px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1404">
                Level 3
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1405">
                Specialized Guides
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1406">
                19 Deep Dives
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1407">
              Expert-level topics for those who want comprehensive protection.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1408">
              {['AI Safety Primer', 'Digital Legacy Planning', 'Identity Masking', 'Cloud Storage Security', 'Smart Home Protection', 'Travel Security', 'Social Media Privacy', 'Data Broker Removal'].map((topic, index) => <div key={index} className="bg-[#f8f8f8] rounded-lg p-4 border border-gray-200 text-center" data-id="element-1409">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-1410">
                    {topic}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Module Preview */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1411">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1412">
          <div className="text-center mb-12" data-id="element-1413">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1414">
              See what a lesson looks like
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1415">
              Clear instructions. Visual guides. No confusion.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-1416">
            {/* Module Header */}
            <div className="bg-[#1a0647] p-6" data-id="element-1417">
              <div className="flex items-center justify-between" data-id="element-1418">
                <div data-id="element-1419">
                  <span className="text-[#00f4f8] text-sm font-semibold" data-id="element-1420">
                    Level 1 • Module 2
                  </span>
                  <h3 className="text-white text-xl font-bold mt-1" data-id="element-1421">
                    Recognizing Scams & Phishing
                  </h3>
                </div>
                <div className="text-white text-right" data-id="element-1422">
                  <span className="text-sm opacity-70" data-id="element-1423">Progress</span>
                  <div className="text-lg font-bold" data-id="element-1424">3 of 8 steps</div>
                </div>
              </div>
              <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2" data-id="element-1425">
                <div className="bg-[#00f4f8] h-2 rounded-full" style={{
                width: '37.5%'
              }} data-id="element-1426"></div>
              </div>
            </div>

            {/* Module Content */}
            <div className="p-8" data-id="element-1427">
              <h4 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1428">
                Step 3: Check the sender's email address
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1429">
                Scammers often use email addresses that look similar to real
                companies but have small differences. Here's how to spot them:
              </p>

              {/* Visual Example */}
              <div className="bg-[#f8f8f8] rounded-xl p-6 mb-6 border-2 border-gray-200" data-id="element-1430">
                <p className="text-sm font-semibold text-gray-500 mb-4" data-id="element-1431">
                  EXAMPLE: Spot the fake
                </p>
                <div className="space-y-3" data-id="element-1432">
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-1433">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-1434" />
                    <span className="font-mono text-sm" data-id="element-1435">
                      support@amazon.com
                    </span>
                    <span className="ml-auto text-green-600 text-sm font-medium" data-id="element-1436">
                      Real
                    </span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-1437">
                    <span className="text-red-500 mr-3" data-id="element-1438">✗</span>
                    <span className="font-mono text-sm" data-id="element-1439">
                      support@amaz0n-secure.com
                    </span>
                    <span className="ml-auto text-red-600 text-sm font-medium" data-id="element-1440">
                      Fake
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-1441">
                <button className="text-gray-500 font-medium hover:text-[#631bb7] transition-colors" data-id="element-1442">
                  ← Previous Step
                </button>
                <button className="bg-[#631bb7] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#521099] transition-colors" data-id="element-1443">
                  Next Step →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1444">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1445">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1446">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1447">
              Ready to start learning?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-1448">
              Take your free assessment and get your personalized learning path
              in under 3 minutes.
            </p>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1449">
              Start My Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer data-id="element-1450" />
    </div>;
}